<script lang="ts">
  // Audit by Hash sub-panel per INTERFACE-SPECIFICATION.md §10.5.
  //
  // Paste a catalogue hash; the read-path Worker resolves the manifest
  // entry and serves the cube state for that hash. The view shows the
  // governance chain plus the resolved metadata. A "reproduce a
  // calculation" form is deferred to PR 5 alongside the audit-trail
  // tooling.

  import { onMount } from 'svelte';
  import { fetchCatalogue, getAllVersions, bootstrapManifest, catalogueState } from '../../lib/catalogue-store.svelte';
  import { PRODUCTS, productKey } from '../../lib/products';
  import { route } from '../../lib/router.svelte';
  import { formatLocalDate, formatEffectiveTimestamp, formatClampedFromNote } from '../../lib/formatters';
  import type { ManifestVersionEntry, Jurisdiction, ProductId } from '../../lib/types';

  let hashInput = $state<string>(route.current.query.get('hash') ?? '');
  let resolvedProduct = $state<{ jurisdiction: Jurisdiction; product: ProductId } | null>(null);
  let entry = $state<ManifestVersionEntry | null>(null);
  let loading = $state<boolean>(false);
  let error = $state<string | null>(null);

  onMount(async () => {
    if (!catalogueState.manifest) await bootstrapManifest();
    if (hashInput) await lookup();
  });

  async function lookup() {
    loading = true;
    error = null;
    resolvedProduct = null;
    entry = null;
    const hash = hashInput.trim();
    for (const p of PRODUCTS) {
      const versions = getAllVersions(catalogueState.manifest, p.jurisdiction, p.product);
      const match = versions.find((v) => v.git_hash === hash);
      if (match) {
        resolvedProduct = { jurisdiction: p.jurisdiction, product: p.product };
        entry = match;
        break;
      }
    }
    if (!resolvedProduct || !entry) {
      error = `No manifest entry found for hash ${hash}. Hashes outside the version manifest can still be inspected directly in GitHub.`;
    } else {
      await fetchCatalogue(resolvedProduct.jurisdiction, resolvedProduct.product, hash);
    }
    loading = false;
  }

  function statusLabel(s: string): string {
    return s === 'in_effect' ? 'In Effect' : s === 'approved' ? 'Approved · queued' : s === 'superseded' ? 'Superseded' : s;
  }
</script>

<section>
  <h3 class="section-heading">Audit by hash</h3>
  <p class="muted">Paste a catalogue hash to reproduce the governed state served at a historical moment.</p>

  <div class="audit-form">
    <label>
      <span class="muted">Hash</span>
      <input type="text" bind:value={hashInput} placeholder="e.g. catalogue/2026-04-01.1 commit SHA" />
    </label>
    <button class="btn" type="button" onclick={lookup} disabled={loading}>{loading ? 'Looking up…' : 'Look up'}</button>
  </div>

  {#if error}
    <div class="banner banner-fail">{error}</div>
  {/if}

  {#if entry && resolvedProduct}
    <h3 class="section-heading">Resolved version</h3>
    <dl class="audit-result">
      <div><dt>Product</dt><dd>{productKey(resolvedProduct)}</dd></div>
      <div><dt>Catalogue version</dt><dd><code>{entry.catalogue_version}</code></dd></div>
      <div><dt>Status</dt><dd>{statusLabel(entry.status)}</dd></div>
      <div>
        <dt>Effective from</dt>
        <dd>
          {formatEffectiveTimestamp(entry.effective_from)}
          {#if entry.proposed_effective_from}
            <div class="clamp-note muted">{formatClampedFromNote(entry.proposed_effective_from)}</div>
          {/if}
        </dd>
      </div>
      <div><dt>Proposed by</dt><dd>{entry.proposed_by ?? '—'}</dd></div>
      <div><dt>Approved by</dt><dd>{entry.approved_by ?? '—'}</dd></div>
      <div><dt>Substantiation</dt><dd>{entry.substantiation ?? '—'}</dd></div>
    </dl>
    <p class="muted">"Reproduce a calculation" inputs ship with PR 5 alongside the audit-trail tooling.</p>
  {/if}
</section>

<style>
  .audit-form { display: flex; gap: var(--space-3); align-items: end; flex-wrap: wrap; margin-bottom: var(--space-3); }
  .audit-form input { padding: var(--space-2); border: 1px solid var(--surface-line); border-radius: var(--radius-sm); min-width: 24rem; font-family: var(--font-mono); }
  .audit-result {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: var(--space-3);
  }
  .audit-result dt { font-size: var(--type-sm); color: var(--surface-muted); }
  .audit-result dd { margin: 0; font-size: var(--type-sm); }
  code { font-family: var(--font-mono); }
</style>
